import { FC, ReactNode, useMemo } from 'react';
import { Autocomplete, Box, TextField, Typography, createFilterOptions } from '@mui/material';
import {
	IPhoneCountry,
	PHONE_COUNTRIES,
	phoneCountryByCode
} from '@/constants/phone-country-codes';

export interface PhoneCountryCodeFieldProps {
	/** Valoarea din formular — DOAR prefixul, ex. „+40". */
	value: string;
	/** Primește tot prefixul (string), sau „" la golire. Niciodată obiectul țării. */
	onChange: (value: string) => void;
	label?: string;
	disabled?: boolean;
	error?: boolean;
	helperText?: ReactNode;
	fullWidth?: boolean;
	size?: 'small' | 'medium';
}

const Flag: FC<{ iso: string }> = ({ iso }) =>
	iso ? (
		<Box
			component="span"
			className={`fi fi-${iso}`}
			sx={{ borderRadius: 0.5, flexShrink: 0, boxShadow: '0 0 0 1px rgba(0,0,0,0.08)' }}
		/>
	) : null;

const filterOptions = createFilterOptions<IPhoneCountry>({
	stringify: (option) => `${option.name} ${option.phoneCode}`
});

const optionLabel = (option: IPhoneCountry): string =>
	option.iso ? `${option.name} ${option.phoneCode}` : option.phoneCode;

// Dropdown cu autocomplete pentru prefixul telefonic (steag + nume RO + prefix).
// Valoarea rămâne mereu string-ul prefixului — nu modifică payload-ul către BE.
export const PhoneCountryCodeField: FC<PhoneCountryCodeFieldProps> = ({
	value,
	onChange,
	label,
	disabled,
	error,
	helperText,
	fullWidth,
	size
}) => {
	// Opțiunea selectată se rezolvă din `value`. Dacă prefixul nu e în listă (valoare
	// custom din BE), îl păstrăm ca opțiune sintetică pentru a-l afișa corect.
	const { options, selected } = useMemo(() => {
		const match = phoneCountryByCode(value);
		if (match) return { options: PHONE_COUNTRIES, selected: match };
		if (value) {
			const synthetic: IPhoneCountry = { name: value, iso: '', phoneCode: value };
			return { options: [synthetic, ...PHONE_COUNTRIES], selected: synthetic };
		}
		return { options: PHONE_COUNTRIES, selected: null };
	}, [value]);

	return (
		<Autocomplete<IPhoneCountry>
			options={options}
			value={selected}
			disabled={disabled}
			fullWidth={fullWidth}
			size={size}
			autoHighlight
			filterOptions={filterOptions}
			getOptionLabel={optionLabel}
			isOptionEqualToValue={(option, val) => option.phoneCode === val.phoneCode}
			onChange={(_e, option) => onChange(option ? option.phoneCode : '')}
			renderOption={(props, option) => (
				<Box component="li" {...props} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
					<Flag iso={option.iso} />
					<Typography variant="body2" sx={{ flexGrow: 1 }}>
						{option.name}
					</Typography>
					<Typography variant="body2" sx={{ color: 'text.secondary' }}>
						{option.phoneCode}
					</Typography>
				</Box>
			)}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					error={error}
					helperText={helperText}
					slotProps={{
						...params.slotProps,
						input: {
							...params.slotProps.input,
							startAdornment: selected?.iso ? (
								<>
									<Box sx={{ display: 'flex', alignItems: 'center', pl: 0.5 }}>
										<Flag iso={selected.iso} />
									</Box>
									{params.slotProps.input.startAdornment}
								</>
							) : (
								params.slotProps.input.startAdornment
							)
						}
					}}
				/>
			)}
		/>
	);
};

PhoneCountryCodeField.defaultProps = {
	label: undefined,
	disabled: false,
	error: false,
	helperText: undefined,
	fullWidth: true,
	size: 'medium'
};
